import { useCallback } from 'react';
import { useAppSelector } from '@/store';
import { useToast } from '@/contexts/admin/ToastContext';
import { useSubmitMaintenanceTicket } from './useSubmitMaintenanceTicket';
import { findCategoryForSubCategory } from '@/utils/residents/maintenance/maintenance';
import { IMaintenanceInfoResponse } from '@/store/api/maintenanceApi';
import { PermissionChoice } from './useMaintenanceForm';
import { extractErrorMessage } from '@/utils/common/errorHandling';

export interface MaintenanceSubmitArgs {
  requestType: 'maintenance' | 'it';
  category: string;
  subCategory: string;
  maintenanceInfo: IMaintenanceInfoResponse | undefined;
  location: string;
  problem: string;
  severity: string;
  title: string;
  description: string;
  phoneNumber: string;
  permissionChoice: PermissionChoice;
  attachments: any[];
  hasPets?: boolean;
  specialInstructions?: string;
}

export interface MaintenanceSubmitResult {
  success: boolean;
  error?: any;
}

/**
 * Hook to handle maintenance ticket submission logic
 * Consolidates duplicate submission logic from mobile and desktop forms
 */
export const useMaintenanceSubmit = () => {
  const { submit, status } = useSubmitMaintenanceTicket();
  const { showSuccess, showError } = useToast();
  const user = useAppSelector((s) => s.auth.user as any);

  const submitTicket = useCallback(async (args: MaintenanceSubmitArgs): Promise<MaintenanceSubmitResult> => {
    try {
      // Determine the category based on request type
      let finalCategory = '';
      if (args.requestType === 'it') {
        finalCategory = 'IT';
      } else if (args.category) {
        // If category was explicitly set
        finalCategory = args.category;
      } else if (args.subCategory) {
        // Derive category from subcategory
        finalCategory = findCategoryForSubCategory(args.maintenanceInfo, args.subCategory);
      }

      // Fallback: use first available category if we still don't have one
      if (!finalCategory && args.maintenanceInfo?.categories?.length) {
        finalCategory = args.maintenanceInfo.categories[0].name;
      }

      // Ensure we have a phone number - use user's phone from the form hook
      // If still empty, the backend will use account.phoneNumber
      const finalPhoneNumber = args.phoneNumber || user?.phoneNumber || '';
      
      // Build petsDescription from hasPets and specialInstructions
      let petsDescription: string | undefined;
      if (args.hasPets || args.specialInstructions?.trim()) {
        const parts: string[] = [];
        if (args.hasPets) parts.push('Has pets.');
        if (args.specialInstructions?.trim()) parts.push(args.specialInstructions.trim());
        petsDescription = parts.join(' ');
      }

      await submit({
        maintenanceInfo: args.maintenanceInfo,
        locationName: args.location,
        categoryName: finalCategory,
        subCategoryName: args.subCategory,
        problemName: args.problem,
        severityName: args.severity,
        description: `${args.title}${args.description ? '\n\n' + args.description : ''}`,
        phoneNumber: finalPhoneNumber,
        permissionChoice: args.permissionChoice,
        attachments: args.attachments,
        petsDescription,
      });
      
      showSuccess('Work request submitted successfully');
      return { success: true };
    } catch (e) {
      console.error('Failed to submit maintenance request:', e);
      const errorMessage = extractErrorMessage(e, 'Failed to submit work request. Please try again.');
      showError(errorMessage);
      return { success: false, error: e };
    }
  }, [submit, user, showSuccess, showError]);

  return { submitTicket, status };
};
